var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Label, Text } from "../../tremor/Text";
import { Icon } from "../../tremor/Icon";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { toast, Toaster } from "sonner";
import { QuestionMessage } from "../../components/QuestionMessage";
import { QuestionHistory } from "../../components/QuestionHistory";
import React from "react";
import { useBackend } from "../Wrapper";
import { useDashboard } from "../Dashboard/useDashboard";
import { ChartLoader } from "../../components/ChartLoader";
import { LogType } from "@onvo-ai/js";
import { useTheme } from "../Dashboard/useTheme";
import { PromptInput } from "../../components/PromptInput";
import { useMaxHeight } from "../../lib/useMaxHeight";
import { ChevronDoubleRightIcon, ClockIcon, PencilSquareIcon, ViewfinderCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { create } from "zustand";
import { Tooltip } from "../../tremor/Tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../../tremor/DropdownMenu";
import { useIsMobile } from "../../lib/utils";
dayjs.extend(relativeTime);
export const useCopilot = create((set) => ({
    open: false,
    variant: "default",
    setVariant: (variant) => {
        localStorage.setItem("onvo-copilot-variant", variant);
        set({ variant });
    },
    setOpen: (op) => {
        let variant = localStorage.getItem("onvo-copilot-variant");
        if (variant) {
            set({ open: op, variant: variant });
        }
        else {
            set({ open: op, variant: "default" });
        }
    }
}));
const CopilotRaw = ({ coupled }) => {
    var _a, _b, _c, _d;
    const { backend, setContainerRef, } = useBackend();
    const { dashboard, refreshWidgets, tab: dashboardTab } = useDashboard();
    const theme = useTheme();
    const { open, setOpen, setVariant, variant } = useCopilot();
    const scroller = useRef(null);
    const localRef = useRef(null);
    const { lg, sm } = useMaxHeight();
    const isMobile = useIsMobile();
    const [questionLoading, setQuestionLoading] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState();
    const [messages, setMessages] = useState([]);
    const [userQuery, setUserQuery] = useState("");
    useEffect(() => {
        if (isMobile) {
            setVariant("modal");
        }
    }, [isMobile]);
    useEffect(() => {
        if ((localRef === null || localRef === void 0 ? void 0 : localRef.current) && !coupled) {
            setContainerRef(localRef);
        }
    }, [localRef, coupled]);
    useEffect(() => {
        if (!window.Onvo) {
            window.Onvo = {};
        }
        window.Onvo.setCopilotOpen = (val) => setOpen(val);
    }, []);
    useEffect(() => {
        if (!open) {
            setMessages([]);
            setSelectedQuestion(undefined);
            setUserQuery("");
        }
    }, [open]);
    useEffect(() => {
        if (selectedQuestion) {
            getMessages(selectedQuestion.id);
        }
        else {
            setMessages([]);
        }
    }, [selectedQuestion, userQuery]);
    const getMessages = (id) => __awaiter(void 0, void 0, void 0, function* () {
        let msg = yield (backend === null || backend === void 0 ? void 0 : backend.question(id).getMessages());
        if (!msg || msg.length === 0) {
            if (userQuery && userQuery.trim() !== "") {
                msg = [{
                        role: "user",
                        content: userQuery,
                        created_at: dayjs().toISOString(),
                        id: "new",
                    }];
            }
        }
        setMessages(msg && msg.length > 0 ? msg : []);
        setTimeout(scrollToBottom, 100);
    });
    const scrollToBottom = () => {
        if (scroller.current) {
            scroller.current.scrollTop = scroller.current.scrollHeight;
        }
    };
    const addToDashboard = (widget, library) => __awaiter(void 0, void 0, void 0, function* () {
        if (!backend)
            return;
        toast.promise(() => __awaiter(void 0, void 0, void 0, function* () {
            let wid = yield backend.widgets.create(Object.assign(Object.assign({}, widget), { use_in_library: library || false, use_in_chat: false, tab: dashboardTab || 0, layouts: {
                    lg: {
                        x: 0,
                        y: lg,
                        w: 4,
                        h: widget.type === "metric" ? 8 : 20,
                    },
                    sm: {
                        x: 0,
                        y: sm,
                        w: 3,
                        h: widget.type === "metric" ? 8 : 20,
                    },
                } }));
            yield backend.widget(wid.id).updateCache();
            return wid;
        }), {
            loading: library
                ? "Adding widget to library..."
                : "Adding widget to dashboard...",
            success: (widget) => {
                refreshWidgets(backend);
                setSelectedQuestion(undefined);
                setMessages([]);
                setOpen(false);
                setUserQuery("");
                if (backend) {
                    backend.logs.create({
                        type: LogType.EditWidget,
                        dashboard: widget.dashboard,
                        widget: widget.id,
                    });
                }
                if (library) {
                    return "Widget added to library";
                }
                else {
                    return "Widget added to dashboard";
                }
            },
            error: (error) => library
                ? "Error adding widget to library: " + error.message
                : "Error adding widget to dashboard: " + error.message,
        });
    });
    const createQuestion = (query) => __awaiter(void 0, void 0, void 0, function* () {
        if (!dashboard)
            return;
        try {
            let response = yield (backend === null || backend === void 0 ? void 0 : backend.questions.create({
                dashboard: dashboard === null || dashboard === void 0 ? void 0 : dashboard.id,
                query: query,
            }));
            if (!response) {
                throw new Error("Failed to create question.");
            }
            handleAskQuestion(query, response.id);
            setUserQuery(query);
            setSelectedQuestion(response);
        }
        catch (e) {
            toast.error("Failed to create question: ", e.message);
        }
    });
    const handleAskQuestion = (query, questionId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!questionId) {
            createQuestion(query);
            return;
        }
        const qId = questionId || (selectedQuestion === null || selectedQuestion === void 0 ? void 0 : selectedQuestion.id);
        if (!qId)
            return;
        try {
            setQuestionLoading(true);
            yield (backend === null || backend === void 0 ? void 0 : backend.question(qId).completion(query, log => {
                getMessages(qId);
                setUserQuery("");
            }));
            getMessages(qId);
            setQuestionLoading(false);
        }
        catch (error) {
            console.error("Error asking question:", error);
            toast.error(error.message, {
                duration: Infinity,
                closeButton: true
            });
            setQuestionLoading(false);
        }
    });
    const handleEditMessage = (messageId, content) => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedQuestion)
            return;
        try {
            setQuestionLoading(true);
            const editedMessage = messages.find((msg) => msg.id === messageId);
            setMessages((prev) => {
                return prev.filter((msg) => {
                    if (msg.created_at && (editedMessage === null || editedMessage === void 0 ? void 0 : editedMessage.created_at) && msg.created_at > editedMessage.created_at) {
                        return false;
                    }
                    return true;
                });
            });
            // waitForJob
            let response = yield (backend === null || backend === void 0 ? void 0 : backend.question(selectedQuestion.id).updateMessage(messageId, content));
            if (!response) {
                toast.error("Failed to edit message.");
                setQuestionLoading(false);
                return;
            }
            getMessages(selectedQuestion.id);
            setQuestionLoading(false);
        }
        catch (error) {
            console.error("Error asking question:", error);
            setQuestionLoading(false);
        }
    });
    const questionMessageList = useMemo(() => {
        return messages.map((message, index) => (React.createElement(QuestionMessage, { key: message.id, questionLoading: questionLoading, message: message, isLast: index === messages.length - 1, onReply: (msg) => {
                handleAskQuestion(msg);
            }, onEdit: (msg) => {
                handleEditMessage(message.id, msg);
            }, onAdd: (widget, library) => {
                addToDashboard(widget, library);
            } })));
    }, [messages, questionLoading]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: localRef, className: "onvo-root-style onvo-copilot-modal onvo-@container/questionmodal onvo-foreground-color onvo-animate-dialogOpen onvo-z-40 onvo-overflow-hidden " + (variant === "default" ? "onvo-h-full onvo-relative onvo-w-full" : "onvo-fixed onvo-h-[640px] onvo-w-[420px] onvo-right-5 onvo-bottom-5 onvo-rounded-3xl onvo-border-solid onvo-border onvo-border-slate-200 dark:onvo-border-slate-800 onvo-shadow-xl") },
            !coupled && React.createElement(Toaster, { position: "bottom-right", richColors: true }),
            React.createElement("div", { className: "onvo-question-modal-question-list onvo-flex onvo-flex-col onvo-foreground-color onvo-absolute onvo-w-full onvo-right-0 onvo-top-0 onvo-z-20 onvo-h-full" },
                React.createElement("div", { className: "onvo-foreground-color onvo-relative onvo-h-auto onvo-top-0 onvo-w-full onvo-z-10 onvo-flex onvo-flex-row onvo-justify-between onvo-items-center onvo-gap-2 onvo-border-solid onvo-border-b onvo-px-3 onvo-border-black/10 onvo-py-2 dark:onvo-border-white/10" },
                    React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-w-full @xl/questionmodal:onvo-w-auto onvo-gap-0 onvo-justify-center onvo-items-start" },
                        React.createElement(Label, null, ((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.copilot_title) || "Copilot"),
                        (((_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.copilot_description) || "").trim() !== "" && (React.createElement(Text, { className: "onvo-text-xs" }, ((_c = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _c === void 0 ? void 0 : _c.copilot_description) || ""))),
                    React.createElement(Tooltip, { content: "New chat", side: "bottom" },
                        React.createElement(Icon, { icon: PencilSquareIcon, className: "onvo-relative onvo-border-black/10 onvo-background-color dark:onvo-border-white/10", onClick: () => {
                                setSelectedQuestion(undefined);
                                setMessages([]);
                            } })),
                    React.createElement(DropdownMenu, null,
                        React.createElement(Tooltip, { triggerAsChild: true, content: "History", side: "bottom" },
                            React.createElement(DropdownMenuTrigger, { asChild: true },
                                React.createElement(Icon, { icon: ClockIcon, className: "onvo-relative onvo-border-black/10 onvo-background-color dark:onvo-border-white/10" }))),
                        React.createElement(DropdownMenuContent, { side: "bottom", className: "onvo-backdrop-blur-lg onvo-min-w-56 onvo-z-50 onvo-overflow-y-auto onvo-max-h-[300px]" },
                            React.createElement(QuestionHistory, { onSelect: (q) => {
                                    setSelectedQuestion(q);
                                    setMessages([]);
                                }, variant: "dropdown" }))),
                    coupled && !isMobile && (React.createElement(Tooltip, { content: variant === "modal" ? "Show as sidebar" : "Show as modal", side: "bottom" },
                        React.createElement(Icon, { icon: ViewfinderCircleIcon, className: "onvo-relative onvo-border-black/10 onvo-background-color dark:onvo-border-white/10", onClick: () => {
                                setVariant(variant === "default" ? "modal" : "default");
                            } }))),
                    React.createElement(Tooltip, { content: "Close copilot", side: "bottom" },
                        React.createElement(Icon, { icon: variant === "default" ? ChevronDoubleRightIcon : XMarkIcon, className: "relative onvo-border-black/10 onvo-background-color dark:onvo-border-white/10", onClick: () => {
                                setOpen(false);
                            } }))),
                React.createElement("div", { className: "onvo-flex onvo-flex-grow onvo-w-full onvo-h-[calc(100%-52px)] onvo-overflow-y-auto onvo-scrollbar-thin onvo-flex-col " },
                    React.createElement("div", { className: "onvo-w-full onvo-h-full onvo-flex onvo-flex-col onvo-px-2" },
                        !selectedQuestion ? (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "onvo-flex-grow onvo-w-full" }),
                            React.createElement("div", { className: "onvo-z-50 onvo-left-0 onvo-overflow-x-hidden onvo-flex-shrink-0 onvo-transition-all onvo-ease-in-out onvo-mb-2" },
                                React.createElement(QuestionHistory, { onSelect: (q) => {
                                        setSelectedQuestion(q);
                                        setMessages([]);
                                    }, limit: 4, variant: "list" })))) : (React.createElement("div", { className: "onvo-flex onvo-w-full onvo-flex-grow onvo-flex-col onvo-gap-4 onvo-overflow-y-auto onvo-scrollbar-thin onvo-py-2", ref: scroller },
                            React.createElement("div", { className: "onvo-flex onvo-flex-col onvo-relative onvo-mx-auto onvo-w-full onvo-max-w-screen-md" },
                                questionMessageList,
                                questionLoading && (React.createElement(ChartLoader, { id: selectedQuestion === null || selectedQuestion === void 0 ? void 0 : selectedQuestion.id }))))),
                        React.createElement(PromptInput, { url: (_d = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _d === void 0 ? void 0 : _d.help_url, className: "onvo-mb-2", onSubmit: val => {
                                handleAskQuestion(val);
                            } })))))));
};
export const Copilot = ({ coupled }) => {
    const { variant, open, setVariant } = useCopilot();
    useEffect(() => {
        let storedVariant = localStorage.getItem("onvo-copilot-variant");
        if (!coupled) {
            setVariant("modal");
            return;
        }
        if (storedVariant) {
            setVariant(storedVariant);
        }
        else {
            setVariant("default");
        }
    }, []);
    if (variant === "default") {
        return (React.createElement(CopilotRaw, { coupled: coupled }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("dialog", { open: open },
            React.createElement(CopilotRaw, { coupled: coupled }))));
};
//# sourceMappingURL=Copilot.js.map